import { NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { ALL_LOCATIONS, PROVINCES } from '@/app/lib/constants'
import { routes } from '@/app/lib/routing'

/** Ranking: siempre como máximo 10 puestos (research.md §1, FR-017) —
 * paridad con la referencia real (`eci-firefly-uikit/ProductosLocalizados`,
 * `MAX_ITEMS = 10`). */
export const MAX_ITEMS = 10

/** Mensaje mostrado en el lugar del carrusel cuando no hay tarjetas
 * utilizables (0 productos tras `toProductCards`) — la cabecera y el enlace
 * "ver todos" siguen visibles, solo se sustituye el carrusel. */
export const EMPTY_STATE_MESSAGE = 'No hay eventos'

/**
 * A diferencia del resto del catálogo (`hasLink = linkTitle !== '' &&
 * linkUrl !== ''`, sin enlace si `link_title` llega vacío), este módulo
 * SIEMPRE muestra un enlace: con `link_title` vacío, usa este texto fijo en
 * vez de ocultarlo (FR-010, research.md §6).
 */
export const FALLBACK_LINK_TITLE = 'VER TODOS LOS PRODUCTOS MÁS VENDIDOS'

/** Destino fijo del enlace de fallback — siempre "lo más vendido, toda
 * España", con independencia de `link_url` (FR-010, research.md §6). */
export const FALLBACK_LINK_HREF = routes.loMasVendido(ALL_LOCATIONS)

/**
 * Etiqueta visible del selector de localización, solo para este módulo: la
 * fuente canónica compartida `PROVINCES` usa "España (todas)" para el slug
 * `todos` (reutilizada por el resto del sitio); este módulo, por fidelidad al
 * diseño de referencia, muestra "toda España" en su lugar — override de
 * *presentación* local, no una segunda lista de provincias (research.md §3,
 * spec.md Clarifications).
 */
export const LOCATION_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  [ALL_LOCATIONS]: 'toda España',
}

/** Etiqueta visible de un slug de localización, aplicando
 * `LOCATION_LABEL_OVERRIDES` antes de caer a `PROVINCES` — compartida entre
 * `LocationPicker` (lista completa) y `ProductosLocalizadosHeader` (título
 * dinámico), para no duplicar esta resolución en dos sitios. */
export function labelForLocation(slug: string): string {
  return LOCATION_LABEL_OVERRIDES[slug] ?? PROVINCES.find((province) => province.slug === slug)?.label ?? slug
}

/**
 * Prefijo del título dinámico cuando `title` llega vacío (FR-001/FR-003): el
 * selector de localización (`LocationPicker`) se pinta inline justo después,
 * como palabra final de la frase — es el propio botón el que ya muestra la
 * localización ("Entradas de lo más vendido en" + **toda España** clicable),
 * nunca un texto duplicado (corrección de validación visual: la localización
 * NO se repite fuera del botón).
 */
export const DYNAMIC_TITLE_PREFIX = 'Entradas de lo más vendido en'

/**
 * Nombre accesible del `LoadingOverlay` (spinner general del sitio, ver
 * `@/app/components/shared/LoadingOverlay`) mientras `ProductosLocalizadosHeader`
 * navega tras un cambio de localización. El click que lo dispara ocurre en un
 * `<button>` del `LocationPicker`, no en un `<a>`, así que el `NavigationProgress`
 * global (que solo detecta clicks sobre `a[href]`) nunca lo muestra por su cuenta
 * — este módulo pilota el mismo overlay explícitamente vía `useTransition`.
 */
export const LOCATION_CHANGE_LOADING_LABEL = 'Cargando productos de la nueva localización'

/**
 * Analítica (`components-fingerprint/Grid Cards.csv`/`Grid destacados.csv` +
 * ejemplo real de `guides/02_Snippets&DataLayer.md` §6, que usa literalmente
 * `module_name: 'productos_localizados'`/`module_type: 'product_list_localized'`
 * para este mismo módulo en la Home). Módulo transversal: aplica igual en
 * cualquier página que lo monte.
 */
export const ANALYTICS_MODULE_NAME = 'productos_localizados'
export const ANALYTICS_MODULE_TYPE = 'product_list_localized'

/** `page_type` de la Home — la única página donde la guía documenta este módulo. */
export const ANALYTICS_HOME_PAGE_TYPE = 'home'

/**
 * `module_position`: el `'2'` fijo es el valor LITERAL del ejemplo real de
 * `02_Snippets&DataLayer.md` §6 para este módulo en la Home (tras
 * HeroCarousel=1) — no es una convención inventada.
 */
export const ANALYTICS_MODULE_POSITION = '2'

/**
 * `module_position` según la página anfitriona. Home: `'2'` (ejemplo de la
 * guía). Fuera de Home (categoría/provincia/evento) el orden depende del CMS y
 * `ModulesAdminRenderer` no numera sus módulos (solo pasa `{ content }`), así
 * que no hay posición real disponible: `not_set` en vez de inventar un número.
 * ⚠️ PENDIENTE de confirmar con Analítica (valor fuera de Home).
 */
export function buildAnalyticsModulePosition(pageType: string = ANALYTICS_HOME_PAGE_TYPE): string {
  return pageType === ANALYTICS_HOME_PAGE_TYPE ? ANALYTICS_MODULE_POSITION : NOT_SET
}

/**
 * `item_list_id`/`item_list_name` de `view_item_list`/`select_item` — mismo
 * PATRÓN del ejemplo real de la guía (`home_productos_localizados_espana` /
 * `entradas_de_lo_mas_vendido_en_espana`), parametrizado por `location` y por
 * `page_type` de la página anfitriona (prefijo): Home → `home_…` (idéntico al
 * ejemplo de la guía), categoría/provincia → `plp_…`, etc.
 * ⚠️ PENDIENTE de confirmar con Analítica el prefijo fuera de Home (se usa el
 * `page_type` de la página tal cual, p. ej. `plp_productos_localizados_madrid`).
 */
export function buildAnalyticsItemListId(
  location: string,
  pageType: string = ANALYTICS_HOME_PAGE_TYPE,
): string {
  return `${slugify(pageType)}_productos_localizados_${slugify(location)}`
}
export function buildAnalyticsItemListName(location: string): string {
  return `entradas_de_lo_mas_vendido_en_${slugify(labelForLocation(location))}`
}

/**
 * Analítica del `LocationPicker` de este módulo (`Grid Cards.csv` →
 * "Apertura del dropdown"/"Selección de una provincia") — valores LITERALES
 * del CSV: `component_id`/`component_label` fijos, sin variación por
 * localización (a diferencia de `selected_value`, que sí es dinámico —
 * ver `LocationPicker.tsx`).
 */
export const LOCATION_PICKER_ANALYTICS_MODULE_NAME = 'selector_provincias'
export const LOCATION_PICKER_ANALYTICS_COMPONENT_ID = 'province_selector'
export const LOCATION_PICKER_ANALYTICS_COMPONENT_LABEL = 'entradas_en_espana'
